import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { requestForToken, onMessageListener } from '@/lib/firebase';
import { useToast } from './use-toast';

/**
 * Detects if current browser is running on a mobile device.
 * Uses userAgent + touch + screen width for high accuracy.
 */
function detectDeviceType(): 'mobile_web' | 'desktop_web' {
  const ua = navigator.userAgent || '';
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth < 768;
  return (isMobileUA || (hasTouchScreen && isSmallScreen)) ? 'mobile_web' : 'desktop_web';
}

export const useFcmToken = (vapidKey: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [token, setToken] = useState<string | null>(null);

  const registerToken = useCallback(async () => {
    if (!user || !vapidKey) return;

    try {
      const fcmToken = await requestForToken(vapidKey);
      if (!fcmToken) {
        console.debug('No FCM token generated (User denied or Browser unsupported)');
        return;
      }

      console.debug('FCM Token generated successfully');
      setToken(fcmToken);

      const deviceType = detectDeviceType();

      // Upsert: if same token exists, just update user_id + last_seen
      const { error } = await supabase
        .from('user_fcm_tokens' as any)
        .upsert({
          user_id: user.id,
          fcm_token: fcmToken,
          device_type: deviceType,
          last_seen_at: new Date().toISOString()
        }, { onConflict: 'fcm_token' });

      if (error) {
        console.error('Error saving FCM token to Supabase:', error);
      }
    } catch (err) {
      console.error('Error in FCM registration:', err);
    }
  }, [user, vapidKey]);

  useEffect(() => {
    if (!user || !vapidKey) return;

    // Register token immediately on mount
    registerToken();

    // Re-register token when page becomes visible again
    // This is CRITICAL for mobile: when Android kills the browser tab
    // and user returns, the old token may be stale/UNREGISTERED.
    // This forces a fresh token on every "wake up".
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        registerToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Listen for foreground messages
    const setupListener = async () => {
      try {
        await onMessageListener((payload) => {
          if (payload && payload.notification) {
            console.debug('Foreground message received:', payload);
            toast({
              title: payload.notification.title || 'Notification',
              description: payload.notification.body || '',
            });
          }
        });
      } catch (err) {
        console.warn('FCM Listener failed (non-blocking):', err);
      }
    };
    setupListener();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, vapidKey, toast, registerToken]);

  return token;
};
